import { AxiosError } from 'axios';
import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';
import React, { useContext, useState } from 'react';
import { useParams } from 'react-router';
import { getCustomerAssortment } from 'views/order-feed/api/get-products';
import { CustomerAssortmentFilter } from 'views/order-feed/types/customer-assortment-filter.enum';
import { CustomerAssortment } from 'views/order-feed/types/customer-assortment.type';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import { UseQueryResult } from '@tanstack/react-query';
import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';
import { isValidRouteParam } from 'views/order-feed/utils/is-valid-route-param';

type State = {
  customerAssortmentQuery: UseQueryResult<CustomerAssortment, AxiosError<unknown, unknown>>;
  repeatOrder: {
    data: CreateOrderFormValues | undefined;
    request: (data: CreateOrderFormValues) => void;
  };
};

type MySupplyPlanProviderProps = {
  children: React.ReactNode;
};

const MySupplyPlanContext = React.createContext<State | undefined>(undefined);

const MySupplyPlanProvider: React.FC<MySupplyPlanProviderProps> = ({ children }) => {
  const { siteErpId } = useParams<MySupplyPlanParams>();
  const {
    account: { company },
  } = useAccountState();
  const [repeatOrderData, setRepeatOrderData] = useState<CreateOrderFormValues>();

  const customerAssortmentQuery = useGet(
    ['getCustomerAssortment', siteErpId, CustomerAssortmentFilter.None],
    ({ queryKey }) => getCustomerAssortment(String(queryKey[1]), CustomerAssortmentFilter.DeliveryAcceptanceStatus),
    {
      enabled: !!company?.features?.hasOrderFeed && isValidRouteParam(siteErpId),
    }
  );

  /** Provides repeat-order values to the mounted create-order form. */
  const requestRepeatOrder = (data: CreateOrderFormValues): void => {
    setRepeatOrderData(data);
  };

  return (
    <MySupplyPlanContext.Provider
      value={{
        customerAssortmentQuery,
        repeatOrder: {
          data: repeatOrderData,
          request: requestRepeatOrder,
        },
      }}>
      {children}
    </MySupplyPlanContext.Provider>
  );
};

function useMySupplyPlanState() {
  const context = useContext(MySupplyPlanContext);
  if (context === undefined) {
    throw new Error('useMySupplyPlanState must be used within a MySupplyPlanProvider');
  }
  return context;
}

export { MySupplyPlanProvider, useMySupplyPlanState };
